import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { HeaderService } from '../../../services/header.service';
import { Api } from '../../../api/api';
import {
  adminInsightsControllerGetFundsRaisedV1,
  adminInsightsControllerGetProjectDistributionV1,
  adminInsightsControllerGetInvestorsV1,
  adminInsightsControllerGetPendingApprovalsV1,
} from '../../../api/functions';
import type { PendingProjectItem } from '../../../api/models';

interface ChartData {
  labels: string[];
  datasets: any[];
}

interface ProjectStatus {
  key: string;
  label: string;
  count: number;
  color: string;
}

@Component({
  selector: 'app-platform-insights',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ChartModule,
    ButtonModule,
    SelectModule,
    TableModule,
  ],
  templateUrl: './platform-insights.component.html',
  styleUrl: './platform-insights.component.css',
})
export class PlatformInsightsComponent implements OnInit {
  private readonly headerService = inject(HeaderService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly api = inject(Api);

  // Lifecycle status colors (frontend responsibility)
  private readonly lifecycleStatusColors: Record<string, string> = {
    privateSale: '#cf4bff',
    publicSale: '#18ff43',
    upcoming: '#ffea00',
    funded: '#00aeff',
    refunded: '#ff1818',
  };

  // Lifecycle status translation keys
  private readonly lifecycleStatusLabels: Record<string, string> = {
    privateSale: 'projects.card.status.privateSale',
    publicSale: 'projects.card.status.publicSale',
    upcoming: 'projects.card.status.upcoming',
    funded: 'projects.card.status.funded',
    refunded: 'projects.card.status.refunded',
  };

  // Period options for dropdowns
  readonly periodOptions = [
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Yearly', value: 'yearly' },
  ];

  // Form controls
  totalFundsPeriodControl = new FormControl<'weekly' | 'monthly' | 'yearly'>('monthly');
  totalInvestorsPeriodControl = new FormControl<'weekly' | 'monthly' | 'yearly'>('yearly');

  // Total Funds - Signals
  selectedTotalFundsPeriod = signal<'weekly' | 'monthly' | 'yearly'>('monthly');
  totalFundsAmount = signal(0);
  totalFundsData = signal<ChartData>({ labels: [], datasets: [] });

  // Active Launches - Signals
  totalProjectsCount = signal(0);
  activeLaunchesData = signal<ChartData>({ labels: [], datasets: [] });
  projectStatusList = signal<ProjectStatus[]>([]);

  // Total Investors - Signals
  selectedTotalInvestorsPeriod = signal<'weekly' | 'monthly' | 'yearly'>('monthly');
  totalInvestorsCount = signal(0);
  totalInvestorsData = signal<ChartData>({ labels: [], datasets: [] });

  // Pending Projects - Signal
  pendingProjects = signal<PendingProjectItem[]>([]);

  // Chart options (static, no need for signals)
  totalFundsOptions: any;
  activeLaunchesOptions: any;
  totalInvestorsOptions: any;

  ngOnInit(): void {
    this.headerService.setTitle(this.translateService.instant('platformInsights.title'));
    this.headerService.setSubtitle(this.translateService.instant('platformInsights.subtitle'));

    // Initialize chart options first (static configuration)
    this.initializeChartOptions();

    // Subscribe to form control changes
    this.totalFundsPeriodControl.valueChanges.subscribe((value) => {
      if (value) {
        this.selectedTotalFundsPeriod.set(value);
        this.loadTotalFundsData();
      }
    });

    this.totalInvestorsPeriodControl.valueChanges.subscribe((value) => {
      if (value) {
        this.selectedTotalInvestorsPeriod.set(value);
        this.loadTotalInvestorsData();
      }
    });

    // Load data from API
    this.loadTotalFundsData();
    this.loadProjectDistributionData();
    this.loadTotalInvestorsData();
    this.loadPendingApprovals();
  }

  /**
   * Initialize static chart options (doesn't depend on API data)
   */
  private initializeChartOptions(): void {
    this.totalFundsOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = this.translateService.instant(
                'platformInsights.totalFunds.raisedAmount'
              );
              const value = context.parsed.y;
              return `${label}: $${value.toLocaleString()}`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: '#f2f2f2',
            callback: function (value: any) {
              return '$' + value.toLocaleString();
            },
          },
          grid: {
            color: '#3b393d',
          },
        },
        x: {
          ticks: {
            color: '#f2f2f2',
          },
          grid: {
            color: '#3b393d',
          },
        },
      },
    };

    this.activeLaunchesOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false,
        },
      },
    };

    this.totalInvestorsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: '#f2f2f2',
          },
          grid: {
            color: '#3b393d',
          },
        },
        x: {
          ticks: {
            color: '#f2f2f2',
          },
          grid: {
            color: '#3b393d',
          },
        },
      },
    };
  }

  onTotalFundsPeriodChange(): void {
    this.loadTotalFundsData();
  }

  onTotalInvestorsPeriodChange(): void {
    this.loadTotalInvestorsData();
  }

  selectTotalFundsPeriod(period: 'weekly' | 'monthly' | 'yearly'): void {
    this.selectedTotalFundsPeriod.set(period);
    this.loadTotalFundsData();
  }

  selectTotalInvestorsPeriod(period: 'weekly' | 'monthly' | 'yearly'): void {
    this.selectedTotalInvestorsPeriod.set(period);
    this.loadTotalInvestorsData();
  }

  private loadTotalFundsData(): void {
    this.api
      .invoke(adminInsightsControllerGetFundsRaisedV1, { period: this.selectedTotalFundsPeriod() })
      .then((data) => {
        this.totalFundsAmount.set(data.total);
        this.totalFundsData.set({
          labels: data.labels,
          datasets: [
            {
              label: this.translateService.instant('platformInsights.totalFunds.raisedAmount'),
              data: data.values,
              backgroundColor: '#806edd',
              borderRadius: 4,
            },
          ],
        });
      });
  }

  private loadProjectDistributionData(): void {
    this.api.invoke(adminInsightsControllerGetProjectDistributionV1).then((data) => {
      const statuses = data.distribution.map((item) => ({
        key: item.status,
        label: this.lifecycleStatusLabels[item.status] || item.status,
        count: item.count,
        color: this.lifecycleStatusColors[item.status] || '#888888',
      }));

      this.projectStatusList.set(statuses);
      this.totalProjectsCount.set(data.totalCount);

      this.activeLaunchesData.set({
        labels: statuses.map((s) => this.translateService.instant(s.label)),
        datasets: [
          {
            data: statuses.map((s) => s.count),
            backgroundColor: statuses.map((s) => s.color),
          },
        ],
      });
    });
  }

  private loadTotalInvestorsData(): void {
    this.api
      .invoke(adminInsightsControllerGetInvestorsV1, {
        period: this.selectedTotalInvestorsPeriod(),
      })
      .then((data) => {
        this.totalInvestorsCount.set(data.total);
        this.totalInvestorsData.set({
          labels: data.labels,
          datasets: [
            {
              label: this.translateService.instant('platformInsights.totalInvestors.numberOf'),
              data: data.values,
              borderColor: '#806edd',
              tension: 0,
              pointRadius: 4,
              pointBackgroundColor: '#806edd',
            },
          ],
        });
      });
  }

  private loadPendingApprovals(): void {
    this.api.invoke(adminInsightsControllerGetPendingApprovalsV1).then((data) => {
      this.pendingProjects.set(data.projects);
    });
  }

  /**
   * Navigate to project details page
   */
  openProjectDetails(projectId: string): void {
    this.router.navigate(['/admin/projects', projectId]);
  }
}
